import React from 'react';

import * as S from './TargetPriceChart.styles';
import {
  type TargetPriceChartProps,
  useChartData,
} from './use-chart-data/useChartData';

export const TargetPriceChart = ({
  timeseries,
  targetPrice,
}: TargetPriceChartProps) => {
  const dimensions = {
    width: 440,
    height: 215,
    margin: { top: 5, right: 50, bottom: 40, left: 0 },
  };

  const chartWidth =
    dimensions.width - dimensions.margin.left - dimensions.margin.right;
  const chartHeight =
    dimensions.height - dimensions.margin.top - dimensions.margin.bottom;
  const leftChartWidth = chartWidth / 2;
  const rightChartWidth = chartWidth / 2;

  const { scales, paths, axisData, isBullish, targetBearish } = useChartData({
    timeseries,
    targetPrice,
    leftChartWidth,
    rightChartWidth,
    chartHeight,
  });

  if (!scales || !paths || !axisData) {
    return (
      <S.ChartSVG width={dimensions.width} height={dimensions.height}>
        <S.AxisText
          x={dimensions.width / 2}
          y={dimensions.height / 2}
          textAnchor="middle"
        >
          No data available
        </S.AxisText>
      </S.ChartSVG>
    );
  }

  return (
    <S.ChartSVG width={dimensions.width} height={dimensions.height}>
      <g
        transform={`translate(${dimensions.margin.left}, ${dimensions.margin.top})`}
      >
        {/* Time series line chart (left side) */}
        <S.TimeSeriesLine d={paths.timeseries} $isBullish={isBullish} />

        {/* Vertical divider line */}
        <S.DividerLine
          x1={leftChartWidth}
          y1={0}
          x2={leftChartWidth}
          y2={chartHeight}
        />

        {/* Target price lines (right side) */}
        <S.TargetLine
          d={paths.targetHigh}
          $variant="high"
          $isBearish={targetBearish?.high || false}
        />
        <S.TargetLine
          d={paths.targetAvg}
          $variant="average"
          $isBearish={targetBearish?.average || false}
        />
        <S.TargetLine
          d={paths.targetLow}
          $variant="low"
          $isBearish={targetBearish?.low || false}
        />

        {/* X-axis line */}
        <S.XAxisLine x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} />

        {/* Y-axis labels */}
        {axisData.yAxis.map((tick, i) => (
          <g key={i}>
            <S.AxisLine x1={0} y1={tick.y} x2={chartWidth} y2={tick.y} />
            <S.AxisText
              x={chartWidth + 15}
              y={tick.y}
              dy="0.35em"
              textAnchor="start"
            >
              {tick.label}
            </S.AxisText>
          </g>
        ))}

        {/* X-axis labels */}
        {axisData.xAxis.map((tick, i) => {
          // Determine text anchor based on position
          let textAnchor: 'start' | 'middle' | 'end' = 'middle';

          if (i === 0) {
            textAnchor = 'start'; // First label: left-aligned
          }

          if (i === 2) {
            textAnchor = 'end'; // Last label: right-aligned
          }

          return (
            <S.XAxisText
              key={i}
              x={tick.x}
              y={chartHeight + 25}
              textAnchor={textAnchor}
            >
              {tick.label}
            </S.XAxisText>
          );
        })}
      </g>
    </S.ChartSVG>
  );
};
